import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, UserPlus } from 'lucide-react';

import { api } from '../lib/api.ts';
import { data as formatarData } from '../lib/formato.ts';
import { useAuth } from '../contexto/auth.tsx';
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Selecao,
  Tabela,
} from '../componentes/ui.tsx';

interface UsuarioSistema {
  id: string;
  nome: string;
  email: string;
  perfil: string;
  ativo: boolean;
  criado_em: string;
  ultimo_acesso: string | null;
}

/**
 * Os perfis vêm do cadastro, não de uma lista aqui dentro.
 *
 * Antes eram quatro nomes fixos neste arquivo, e criar um quinto exigia
 * deploy. Agora quem responde quais existem é a tela de Permissões.
 */
interface PerfilCadastrado {
  codigo: string;
  nome: string;
  descricao: string | null;
  usuarios: number;
}

export function Usuarios() {
  const { usuario: eu, ehAdmin } = useAuth();
  const cache = useQueryClient();

  const perfis = useQuery({
    queryKey: ['perfis'],
    // Só o administrador enxerga o cadastro de perfis. Quem não é cai no
    // perfil que a pessoa já tem, sem poder trocar.
    enabled: ehAdmin,
    queryFn: () => api<{ perfis: PerfilCadastrado[] }>('/perfis'),
  });

  const listaDePerfis = perfis.data?.perfis ?? [];
  const descricaoDoPerfil = (codigo: string): string =>
    listaDePerfis.find((p) => p.codigo === codigo)?.descricao ?? '';

  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [perfil, setPerfil] = useState('consulta');
  const [redefinindo, setRedefinindo] = useState<UsuarioSistema | null>(null);
  const [novaSenha, setNovaSenha] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api<{ usuarios: UsuarioSistema[] }>('/auth/usuarios'),
  });

  const invalidar = () => cache.invalidateQueries({ queryKey: ['usuarios'] });

  const criar = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/auth/usuarios', {
        metodo: 'POST',
        corpo: { nome, email, senha, perfil },
      }),
    onSuccess: () => {
      setNome('');
      setEmail('');
      setSenha('');
      setPerfil('consulta');
      setCriando(false);
      void invalidar();
    },
  });

  const alterar = useMutation({
    mutationFn: (v: { id: string; perfil?: string; ativo?: boolean }) =>
      api<{ ok: true }>(`/auth/usuarios/${v.id}`, {
        metodo: 'PATCH',
        corpo: { perfil: v.perfil, ativo: v.ativo },
      }),
    onSuccess: invalidar,
  });

  const redefinir = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/auth/usuarios/${id}/senha`, {
        metodo: 'POST',
        corpo: { novaSenha },
      }),
    onSuccess: () => {
      setRedefinindo(null);
      setNovaSenha('');
    },
  });

  if (isLoading) return <Carregando />;
  if (isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar os usuários">
        <p>{error.message}</p>
      </Aviso>
    );
  }

  return (
    <div className="space-y-6">
      <Aviso tom="info" titulo="Por que os perfis são separados">
        <p>
          Alterar uma faixa de premiação é mexer em folha de pagamento; rodar o fechamento é
          apurar o mês. São responsabilidades diferentes, e por isso um perfil não dá o outro —
          só <strong>administrador</strong> acumula os dois.
        </p>
      </Aviso>

      <Cartao
        titulo={`${data?.usuarios.length ?? 0} usuários`}
        descricao="Alterar perfil ou desativar vale na hora. Toda mudança fica registrada na auditoria."
        acoes={
          <Botao onClick={() => setCriando(!criando)}>
            <UserPlus className="size-4" />
            Novo usuário
          </Botao>
        }
      >
        {criando && (
          <div className="mb-6 rounded border border-slate-200 bg-slate-50 p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Nome" valor={nome} onChange={setNome} />
              <Campo rotulo="E-mail" tipo="email" valor={email} onChange={setEmail} />
              <Campo
                rotulo="Senha provisória"
                tipo="password"
                valor={senha}
                onChange={setSenha}
                dica="Pelo menos 10 caracteres. Passe por um canal seguro e peça para a pessoa trocar em Meu perfil."
              />
              <Selecao
                rotulo="Perfil"
                valor={perfil}
                onChange={setPerfil}
                opcoes={listaDePerfis.map((p) => p.codigo)}
                vazio="Escolha um perfil"
              />
            </div>

            {criar.isError && (
              <div className="mt-4">
                <Aviso tom="erro">
                  <p>{criar.error.message}</p>
                </Aviso>
              </div>
            )}

            <div className="mt-4 flex gap-3">
              <Botao
                onClick={() => criar.mutate()}
                carregando={criar.isPending}
                desabilitado={nome.trim() === '' || email.trim() === '' || senha.length < 10}
              >
                Criar
              </Botao>
              <Botao tipo="secundario" onClick={() => setCriando(false)}>
                Cancelar
              </Botao>
            </div>
          </div>
        )}

        {alterar.isError && (
          <div className="mb-4">
            <Aviso tom="erro">
              <p>{alterar.error.message}</p>
            </Aviso>
          </div>
        )}

        <Tabela
          cabecalho={
            <tr>
              <th className="py-2 pr-3">Nome</th>
              <th className="py-2 pr-3">E-mail</th>
              <th className="py-2 pr-3">Perfil</th>
              <th className="py-2 pr-3">Situação</th>
              <th className="py-2 pr-3">Último acesso</th>
              <th className="py-2" />
            </tr>
          }
        >
          {(data?.usuarios ?? []).map((u) => {
            const souEu = u.id === eu?.id;
            return (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="py-2 pr-3">
                  {u.nome}
                  {souEu && <span className="ml-2 text-xs text-slate-400">(você)</span>}
                </td>
                <td className="py-2 pr-3 text-slate-600">{u.email}</td>
                <td className="py-2 pr-3">
                  <select
                    value={u.perfil}
                    disabled={souEu || alterar.isPending}
                    onChange={(e) => alterar.mutate({ id: u.id, perfil: e.target.value })}
                    title={
                      souEu ? 'Você não pode alterar o próprio perfil' : descricaoDoPerfil(u.perfil)
                    }
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-sm
                               disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                  >
                    {/* O perfil atual entra na lista mesmo se o cadastro ainda
                        não carregou, para o select nunca aparecer vazio. */}
                    {(listaDePerfis.some((p) => p.codigo === u.perfil)
                      ? listaDePerfis
                      : [
                          { codigo: u.perfil, nome: u.perfil, descricao: null, usuarios: 0 },
                          ...listaDePerfis,
                        ]
                    ).map((p) => (
                      <option key={p.codigo} value={p.codigo}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  <Etiqueta tom={u.ativo ? 'bom' : 'ruim'}>{u.ativo ? 'ativo' : 'desativado'}</Etiqueta>
                </td>
                <td className="py-2 pr-3 text-slate-500">
                  {u.ultimo_acesso === null ? 'nunca entrou' : formatarData(u.ultimo_acesso)}
                </td>
                <td className="py-2">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setRedefinindo(u)}
                      title="Redefinir senha"
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
                    >
                      <KeyRound className="size-4" />
                    </button>
                    {!souEu && (
                      <button
                        onClick={() => alterar.mutate({ id: u.id, ativo: !u.ativo })}
                        className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      >
                        {u.ativo ? 'Desativar' : 'Reativar'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </Tabela>
      </Cartao>

      {redefinindo !== null && (
        <Cartao titulo={`Redefinir a senha de ${redefinindo.nome}`}>
          <div className="max-w-md space-y-4">
            <Aviso tom="atencao">
              <p>
                Você vai definir uma senha nova para outra pessoa. Passe por um canal seguro e
                peça para ela trocar em Meu perfil. A ação fica registrada na auditoria.
              </p>
            </Aviso>

            <Campo
              rotulo="Nova senha"
              tipo="password"
              valor={novaSenha}
              onChange={setNovaSenha}
              dica="Pelo menos 10 caracteres."
            />

            {redefinir.isError && (
              <Aviso tom="erro">
                <p>{redefinir.error.message}</p>
              </Aviso>
            )}

            <div className="flex gap-3">
              <Botao
                onClick={() => redefinir.mutate(redefinindo.id)}
                carregando={redefinir.isPending}
                desabilitado={novaSenha.length < 10}
              >
                Redefinir
              </Botao>
              <Botao
                tipo="secundario"
                onClick={() => {
                  setRedefinindo(null);
                  setNovaSenha('');
                }}
              >
                Cancelar
              </Botao>
            </div>
          </div>
        </Cartao>
      )}
    </div>
  );
}
